import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollText, Pause, Play, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiV1, type LogEntry, type LogLevel } from "@/lib/api";

const ALL_LEVELS: LogLevel[] = ["debug", "info", "warn", "error"];

export function LogsPage() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [paused, setPaused] = useState(false);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState<Set<LogLevel>>(
    () => new Set(["info", "warn", "error"]), // debug default aus
  );
  const seqRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyBottomRef = useRef(true);

  useEffect(() => {
    let stopped = false;
    async function poll() {
      while (!stopped) {
        if (!paused) {
          try {
            const r = await apiV1.getLogs(seqRef.current, 500);
            if (r.entries.length > 0) {
              const maxSeq = r.entries[r.entries.length - 1].seq;
              seqRef.current = maxSeq;
              setEntries((prev) => {
                const next = [...prev, ...r.entries];
                if (next.length > 2000) return next.slice(next.length - 2000);
                return next;
              });
            }
          } catch {
            // ignore
          }
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    }
    poll();
    return () => {
      stopped = true;
    };
  }, [paused]);

  useEffect(() => {
    if (stickyBottomRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    stickyBottomRef.current =
      el.scrollHeight - (el.scrollTop + el.clientHeight) < 50;
  }

  function toggleLevel(l: LogLevel) {
    const next = new Set(active);
    if (next.has(l)) next.delete(l);
    else next.add(l);
    setActive(next);
  }

  const counts = useMemo(() => {
    const c: Record<LogLevel, number> = { debug: 0, info: 0, warn: 0, error: 0 };
    for (const e of entries) c[e.level]++;
    return c;
  }, [entries]);

  const filtered = useMemo(
    () =>
      entries.filter(
        (e) =>
          active.has(e.level) &&
          (filter ? e.line.toLowerCase().includes(filter.toLowerCase()) : true),
      ),
    [entries, active, filter],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <ScrollText className="h-3.5 w-3.5 text-muted-foreground" />
          <CardTitle>Server-Logs</CardTitle>
          <span className="font-mono text-[10px] text-muted-foreground/70">
            {filtered.length}/{entries.length}
          </span>
          <div className="flex items-center gap-1">
            {ALL_LEVELS.map((l) => (
              <LevelChip
                key={l}
                level={l}
                count={counts[l]}
                active={active.has(l)}
                onClick={() => toggleLevel(l)}
              />
            ))}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            className="h-8 w-48 rounded border border-input bg-background/60 px-2 font-mono text-xs"
          />
          <Button variant="outline" size="sm" onClick={() => setPaused(!paused)}>
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEntries([]);
              seqRef.current = 0;
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-[70vh] overflow-auto rounded border border-border bg-card/40 p-2 font-mono text-[10.5px] leading-tight"
        >
          {filtered.length === 0 ? (
            <p className="text-muted-foreground">— keine Eintraege —</p>
          ) : (
            filtered.map((e) => (
              <div key={e.seq} className="flex gap-2 py-0.5">
                <span className="shrink-0 text-muted-foreground/60">
                  {fmtTime(e.time)}
                </span>
                <LevelBadge level={e.level} />
                <span className={"min-w-0 break-all " + lineColor(e.level)}>
                  {e.line}
                </span>
              </div>
            ))
          )}
        </div>
        <p className="mt-2 font-mono text-[10px] text-muted-foreground">
          Polling alle 1.5 s · auto-scroll am Ende, Scrollen-nach-oben hält die Position · Level-Chips zum Ein-/Ausblenden
        </p>
      </CardContent>
    </Card>
  );
}

function LevelChip({
  level,
  count,
  active,
  onClick,
}: {
  level: LogLevel;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  const base = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] transition-colors";
  const colors: Record<LogLevel, { on: string; off: string }> = {
    debug: {
      on: "border-muted-foreground/40 bg-muted/40 text-muted-foreground",
      off: "border-border bg-muted/10 text-muted-foreground/40",
    },
    info: {
      on: "border-foreground/30 bg-foreground/10 text-foreground/90",
      off: "border-border bg-muted/10 text-muted-foreground/40",
    },
    warn: {
      on: "border-warning/50 bg-warning/15 text-warning",
      off: "border-border bg-muted/10 text-muted-foreground/40",
    },
    error: {
      on: "border-destructive/60 bg-destructive/15 text-destructive",
      off: "border-border bg-muted/10 text-muted-foreground/40",
    },
  };
  const cls = active ? colors[level].on : colors[level].off;
  return (
    <button type="button" onClick={onClick} className={base + " " + cls}>
      {level}
      <span className="opacity-70">{count}</span>
    </button>
  );
}

function LevelBadge({ level }: { level: LogLevel }) {
  const cls: Record<LogLevel, string> = {
    debug: "bg-muted/60 text-muted-foreground",
    info: "bg-foreground/10 text-foreground/80",
    warn: "bg-warning/20 text-warning",
    error: "bg-destructive/20 text-destructive",
  };
  return (
    <span
      className={
        "inline-block w-12 shrink-0 rounded px-1.5 text-center text-[9px] uppercase tracking-wider " +
        cls[level]
      }
    >
      {level}
    </span>
  );
}

function lineColor(level: LogLevel): string {
  switch (level) {
    case "error":
      return "text-destructive/90";
    case "warn":
      return "text-warning/90";
    case "debug":
      return "text-muted-foreground/80";
    default:
      return "text-foreground/85";
  }
}

function fmtTime(iso: string): string {
  const t = iso.split("T")[1] ?? iso;
  return t.replace("Z", "").slice(0, 12);
}
